import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { MdArrowBack, MdSend, MdCheck, MdClose, MdAttachMoney } from 'react-icons/md';
import api from '../services/api';
import { useModal } from '../context/ModalContext';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.5rem; }

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const DateText = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
`;

const StatusBadge = styled.span`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  background-color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'borrador') return p.theme.colors.textSecondary + '20';
    if (s === 'enviada') return p.theme.colors.primary + '20';
    if (s === 'aprobada') return p.theme.colors.success + '20';
    if (s === 'rechazada') return p.theme.colors.danger + '20';
    return p.theme.colors.textSecondary + '20';
  }};
  color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'borrador') return p.theme.colors.textSecondary;
    if (s === 'enviada') return p.theme.colors.primary;
    if (s === 'aprobada') return p.theme.colors.success;
    if (s === 'rechazada') return p.theme.colors.danger;
    return p.theme.colors.textSecondary;
  }};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.xl};

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const FullCard = styled(Card)`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const CardTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const InfoLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;
`;

const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  padding: ${p => p.theme.spacing.lg} 0 0;
  border-top: 2px solid ${p => p.theme.colors.border};
  margin-top: ${p => p.theme.spacing.md};
`;

const TotalLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
`;

const TotalValue = styled.span`
  font-size: ${p => p.theme.fontSizes.xxl};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 700;
`;

const ActionsCard = styled(Card)`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const ActionsTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const ActionBtns = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background-color: ${p =>
    p.variant === 'success' ? p.theme.colors.success :
    p.variant === 'danger' ? p.theme.colors.danger :
    p.primary ? p.theme.colors.primary :
    p.theme.colors.backgroundLight};
  color: ${p => (p.primary || p.variant) ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p =>
    p.variant === 'success' ? p.theme.colors.success :
    p.variant === 'danger' ? p.theme.colors.danger :
    p.primary ? p.theme.colors.primary :
    p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  display: inline-flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const ConvertedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.success + '15'};
  color: ${p => p.theme.colors.success};
  border: 1px solid ${p => p.theme.colors.success + '40'};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const QuoteDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { confirm, toast } = useModal();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadQuote(); }, [id]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/cotizaciones/${id}`);
      setQuote(res.data.data || res.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar cotizacion');
    } finally {
      setLoading(false);
    }
  };

  const handleEstado = async (newEstado) => {
    setActionLoading(true);
    try {
      await api.put(`/cotizaciones/${id}`, { estado: newEstado });
      await loadQuote();
    } catch (err) {
      toast('Error al cambiar estado: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertir = async () => {
    const ok = await confirm('¿Convertir esta cotización a recibo? Se creará un recibo pendiente de cobro.', {
      title: 'Convertir a Recibo',
      confirmText: 'Convertir',
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/cotizaciones/${id}/convertir`);
      const reciboId = res.data.data?.recibo_id || res.data.recibo_id;
      navigate(`/recibos/${reciboId}`);
    } catch (err) {
      toast('Error al convertir: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Cargando cotizacion...</p>
        </div>
      </Container>
    );
  }

  if (error || !quote) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
          <p>Error: {error || 'Cotizacion no encontrada'}</p>
          <Btn onClick={() => navigate('/cotizaciones')}>Volver a Cotizaciones</Btn>
        </div>
      </Container>
    );
  }

  const items = quote.items || [];
  const total = items.reduce(
    (sum, item) => sum + (parseFloat(item.subtotal) || 0),
    0
  ) || parseFloat(quote.monto_total) || 0;

  const estado = quote.estado?.toLowerCase();

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}>
          <MdArrowBack />
        </BackButton>
        <HeaderInfo>
          <Title>{quote.numero_cotizacion || `COT-${quote.id}`}</Title>
          <StatusBadge status={quote.estado}>
            {quote.estado
              ? quote.estado.charAt(0).toUpperCase() + quote.estado.slice(1)
              : 'N/A'}
          </StatusBadge>
          {quote.fecha_emision && (
            <DateText>
              Emitida: {new Date(quote.fecha_emision).toLocaleDateString('es-MX')}
            </DateText>
          )}
        </HeaderInfo>
      </PageHeader>

      <ContentGrid>
        <Card>
          <CardTitle>Informacion</CardTitle>
          <InfoRow>
            <InfoLabel>Cliente</InfoLabel>
            <InfoValue>{quote.cliente_nombre || 'N/A'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Fecha de Emision</InfoLabel>
            <InfoValue>
              {quote.fecha_emision
                ? new Date(quote.fecha_emision).toLocaleDateString('es-MX')
                : 'N/A'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Valida Hasta</InfoLabel>
            <InfoValue>
              {quote.fecha_validez
                ? new Date(quote.fecha_validez).toLocaleDateString('es-MX')
                : 'N/A'}
            </InfoValue>
          </InfoRow>
          {quote.notas && (
            <InfoRow>
              <InfoLabel>Notas</InfoLabel>
              <InfoValue style={{ whiteSpace: 'pre-wrap' }}>{quote.notas}</InfoValue>
            </InfoRow>
          )}
          {quote.convertida_recibo_id && (
            <InfoRow>
              <InfoLabel>Recibo Generado</InfoLabel>
              <Link to={`/recibos/${quote.convertida_recibo_id}`} style={{ color: 'inherit' }}>
                <ConvertedBadge>
                  <MdCheck />
                  Ver Recibo #{quote.convertida_recibo_id}
                </ConvertedBadge>
              </Link>
            </InfoRow>
          )}
        </Card>

        <ActionsCard>
          <ActionsTitle>Acciones</ActionsTitle>
          <ActionBtns style={{ flexDirection: 'column' }}>
            {estado === 'borrador' && (
              <Btn primary onClick={() => handleEstado('enviada')} disabled={actionLoading}>
                <MdSend />
                Enviar Cotizacion
              </Btn>
            )}
            {estado === 'enviada' && (
              <>
                <Btn variant="success" onClick={() => handleEstado('aprobada')} disabled={actionLoading}>
                  <MdCheck />
                  Aprobar
                </Btn>
                <Btn variant="danger" onClick={() => handleEstado('rechazada')} disabled={actionLoading}>
                  <MdClose />
                  Rechazar
                </Btn>
              </>
            )}
            {estado === 'aprobada' && !quote.convertida_recibo_id && (
              <Btn primary onClick={handleConvertir} disabled={actionLoading}>
                <MdAttachMoney />
                Convertir a Recibo
              </Btn>
            )}
            {estado === 'aprobada' && quote.convertida_recibo_id && (
              <ConvertedBadge>
                <MdCheck />
                Recibo ya generado
              </ConvertedBadge>
            )}
          </ActionBtns>
        </ActionsCard>
      </ContentGrid>

      <FullCard>
        <CardTitle>Items ({items.length})</CardTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>Descripcion</Th>
                <Th>Cantidad</Th>
                <Th>Precio Unitario</Th>
                <Th>Subtotal</Th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, i) => (
                  <tr key={i}>
                    <Td>{item.descripcion || 'N/A'}</Td>
                    <Td>{item.cantidad}</Td>
                    <Td>
                      ${(parseFloat(item.precio_unitario) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </Td>
                    <Td style={{ fontWeight: 600 }}>
                      ${(parseFloat(item.subtotal) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <Td colSpan="4" style={{ textAlign: 'center', color: '#6b7280' }}>
                    Sin items registrados
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
        <TotalSection>
          <TotalLabel>Total:</TotalLabel>
          <TotalValue>
            ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </TotalValue>
        </TotalSection>
      </FullCard>
    </Container>
  );
};

export default QuoteDetail;
